import { useState, useEffect, ReactElement } from "react";
import { useSelector } from "react-redux";
import { useMutation } from "@apollo/client";
import { selectUserId } from "modules/auth/redux/selectors";
import {
  selectCurrentEventId,
  selectIsEventManager,
  selectIsGuestExperienceSurveyEnabled,
} from "modules/event/selectors";
import { trackError } from "modules/monitoring";
import { Errors } from "modules/monitoring/errors";
import { RatingType } from "modules/guestExperienceSurvey/constants";
import {
  SubmitRatingMutation,
  SubmitRatingMutationVariables,
} from "graphql/generated";
import { useStyles } from "./styles";
import Survey from "../Survey/Survey";
import { MUTATE_RATING } from "../../graphql/mutations";

const TWENTY_MINUTES = 20 * 60e3;

const SurveyContainer = (): ReactElement | null => {
  const [visible, setVisible] = useState(false);
  const userId = useSelector(selectUserId);
  const isManager = useSelector(selectIsEventManager);
  const classes = useStyles();
  const [submitRating] = useMutation<
    SubmitRatingMutation,
    SubmitRatingMutationVariables
  >(MUTATE_RATING);
  const currentEventId = useSelector(selectCurrentEventId);
  const isSurveysEnabled = useSelector(selectIsGuestExperienceSurveyEnabled);

  const voted = localStorage.getItem(`survey:${currentEventId}`);

  const onSubmit = async (rating: RatingType) => {
    try {
      if (!currentEventId) {
        return null;
      }

      const { data } = await submitRating({
        variables: { eventId: currentEventId, rating },
      });

      if (!data) {
        return null;
      }

      if (data.rateEvent?.__typename === "GQLError") {
        trackError(data.rateEvent.message, {
          label: Errors.GUEST_EXPERIENCE_SURVEY_MUTATE_EVENT_RATING,
        });

        return null;
      }
      localStorage.setItem(`survey:${currentEventId}`, rating.toString());

      return null;
    } catch (e) {
      trackError(e, {
        label: Errors.GRAPHQL_NETWORK_ERROR,
      });
      return null;
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setVisible(true);
    }, TWENTY_MINUTES);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  if (!visible || !userId || voted || isManager || !isSurveysEnabled) {
    return null;
  }

  return (
    <div className={classes.surveyContainer} data-testid="survey-container">
      <Survey onClose={() => setVisible(false)} onSubmit={onSubmit} />
    </div>
  );
};

export default SurveyContainer;
